import React, { useState } from "react";
import PropTypes from "prop-types";
import WebIcon from "@/components/common/Web_Icons";
import BaseInput from "@/components/common/inputs/Base_Input";
import {
  getInputBorderClass,
  getInputBackgroundClass,
  getFocusClasses,
  getTransitionClasses,
  isInputActive,
  getIconColorClass,
} from "@/helper/input_styles";

const FileInput = ({
  label,
  placeholder,
  icon,
  onChange,
  className = "",
  accept,
  isLoading,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [fileName, setFileName] = useState("");
  const [progress, setProgress] = useState(0);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFileName(file.name);
      onChange(file);

      // Simulate upload progress
      let currentProgress = 0;
      const interval = setInterval(() => {
        currentProgress += 10;
        setProgress(currentProgress);
        if (currentProgress >= 100) {
          clearInterval(interval);
        }
      }, 200);
    }
  };

  // Use shared styling utilities
  const isActive = isInputActive(fileName, isFocused);
  const iconColor = getIconColorClass(isActive);

  // Use the custom upload icon by default
  const defaultIcon = <WebIcon icon="Upload" size="small" />;
  const displayIcon = icon || defaultIcon;

  return (
    <BaseInput label={label} isActive={isActive}>
      <div className="absolute inset-y-0 left-3 flex items-center">
        <span
          className={`pr-3 border-r border-gray-300`}
          data-testid="file-input-icon">
          {React.isValidElement(displayIcon)
            ? React.cloneElement(displayIcon, { className: iconColor })
            : displayIcon}
        </span>
      </div>
      <input
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept={accept}
        data-testid="file-input"
        {...props}
      />
      <div
        className={`w-full py-6 pl-12 pr-10 
          ${getInputBackgroundClass(isActive)}
          ${isLoading ? "opacity-70 cursor-wait" : "cursor-pointer"}
          rounded-md leading-5 text-black placeholder-black 
          ${getInputBorderClass(isActive)}
          ${getFocusClasses()}
          ${getTransitionClasses()} ${className}`}
        onClick={() => !isLoading && document.getElementById(props.id).click()}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        data-testid="file-input-container">
        <span data-testid="file-input-text">
          {isLoading ? "Uploading..." : fileName || placeholder}
        </span>
      </div>
      {(progress > 0 && progress < 100) || isLoading ? (
        <div
          className="absolute bottom-0 left-0 h-1 bg-primary"
          style={{
            width: `${isLoading ? "100%" : progress}%`,
            transition: isLoading ? "width 1s ease-in-out" : "width 0.2s",
          }}
          data-testid="progress-bar"></div>
      ) : null}
    </BaseInput>
  );
};

FileInput.propTypes = {
  label: PropTypes.string,
  placeholder: PropTypes.string,
  icon: PropTypes.element,
  onChange: PropTypes.func,
  className: PropTypes.string,
  accept: PropTypes.string,
  id: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default FileInput;
